import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, ShieldCheck, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/lib/authContext";
import { cn } from "@/lib/utils";

// ---------- Password policy (mirrors backend auth.validate_password_policy) ----------
// Rules (Feb 2026):
//   • Minimum 10 characters, maximum 128 (backend also enforces).
//   • No character-class restrictions.
//   • Reject the fixed common-password list + any password containing "toneguard".
// The visual signal is a 4-step progress bar + a single hint line — the
// checklist UI has been intentionally retired.
const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_MAX_LENGTH = 128;
const COMMON_PASSWORDS = new Set([
    "password",
    "123456789",
    "1234567890",
    "qwerty123",
    "iloveyou",
    "welcome123",
    "admin1234",
    "letmein1",
    "sunshine1",
]);
const FORBIDDEN_SUBSTRING = "toneguard";

function isCommonPassword(password) {
    if (typeof password !== "string") return false;
    const lowered = password.trim().toLowerCase();
    if (COMMON_PASSWORDS.has(lowered)) return true;
    if (lowered.includes(FORBIDDEN_SUBSTRING)) return true;
    return false;
}

/**
 * Bucketize password strength into one of 4 tiers. Returns everything the
 * PasswordStrength component needs to render — no side channel state.
 * Tiers (per spec):
 *   • rejected (common / forbidden)  → 100% muted destructive + "too common" hint
 *   • too short (< 10)               → 25% red   + "Keep going…"
 *   • ok (10–13)                     → 50% amber + "A little longer and you're set."
 *   • strong (14–18)                 → 75% green + "Good to go."
 *   • excellent (19+ OR has space)   → 100% teal + "Good to go."
 * The excellent tier also fires when a space is present, because a phrase
 * (which passes the substring rule) reliably scores higher than a jumble.
 */
function scoreStrength(password) {
    const empty = { tier: "empty", value: 0, label: "", hint: "", tone: "muted" };
    if (typeof password !== "string" || password.length === 0) return empty;
    if (isCommonPassword(password)) {
        return {
            tier: "rejected",
            value: 4,
            label: "Too common",
            hint: "This password is too common — try something more personal.",
            tone: "destructive",
        };
    }
    const len = password.length;
    const hasSpace = /\s/.test(password);
    if (len < PASSWORD_MIN_LENGTH) {
        return {
            tier: "short",
            value: 1,
            label: "Keep going…",
            hint: "Use at least 10 characters — a phrase works great.",
            tone: "destructive",
        };
    }
    // Space anywhere OR ≥19 chars ⇒ Excellent (100% teal). A phrase like
    // "a quick fox" jumps straight here even at 11 chars — the space is a
    // strong entropy signal we reward.
    if (hasSpace || len >= 19) {
        return {
            tier: "excellent",
            value: 4,
            label: "Excellent",
            hint: "Good to go.",
            tone: "teal",
        };
    }
    if (len <= 13) {
        return {
            tier: "ok",
            value: 2,
            label: "Almost there",
            hint: "A little longer and you're set.",
            tone: "amber",
        };
    }
    return {
        tier: "strong",
        value: 3,
        label: "Strong",
        hint: "Good to go.",
        tone: "green",
    };
}

/**
 * Submit gate for signup + reset flows: length ≥ 10 AND not a common password.
 * No character-class check. Kept as a single source of truth so both forms
 * agree on when the button lights up.
 */
function passwordAcceptable(password) {
    if (typeof password !== "string") return false;
    if (password.length < PASSWORD_MIN_LENGTH) return false;
    if (password.length > PASSWORD_MAX_LENGTH) return false;
    if (isCommonPassword(password)) return false;
    return true;
}

// ---------- Brand panel (left side on desktop) ----------
const BrandPanel = () => (
    <div
        data-testid="auth-brand-panel"
        className="hidden lg:flex w-1/2 flex-col justify-between p-12 bg-secondary/40 border-r border-border"
    >
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
                <div className="font-semibold tracking-tight">ToneGuard</div>
            </div>
        </div>
        <div className="space-y-6">
            <div className="space-y-3">
                <h1 className="font-serif text-2xl sm:text-3xl leading-[1.15] tracking-tight">
                    Better conversations
                    <br />
                    start before you hit
                    <br />
                    <span className="italic">Send.</span>
                </h1>
                <p className="text-base text-foreground/80 leading-relaxed max-w-md">
                    ToneGuard helps every message land the way you actually
                    mean it.
                </p>
            </div>
            <p className="text-[15px] opacity-70 leading-relaxed max-w-md">
                Ever sent a message that came across differently than you
                intended? We&apos;ve all been there. ToneGuard is your
                communication safety net — helping you catch unintended tone
                before it becomes misunderstanding, conflict, or regret.
            </p>
            <div className="space-y-3 max-w-md">
                <p className="text-sm font-medium text-foreground/90">
                    Use ToneGuard to:
                </p>
                <ul className="space-y-3 text-sm opacity-80">
                    <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <span>
                            Rewrite difficult conversations with clarity and
                            confidence
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <span>
                            Communicate with emotional intelligence — not just
                            correct grammar
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <span>
                            Catch misunderstandings before they happen
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <span>
                            Build stronger communication habits with
                            personalized insights over time
                        </span>
                    </li>
                </ul>
            </div>
        </div>
        <div className="text-xs opacity-50">
            Good communication is emotional intelligence in action.
        </div>
    </div>
);

// ---------- Google button ----------
const GoogleButton = ({ onClick, label }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid="btn-google-auth"
        className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border border-border bg-card hover:bg-secondary/60 transition-colors text-sm font-medium"
    >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.614z"
            />
            <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.182l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A9 9 0 0 0 9 18z"
            />
            <path
                fill="#FBBC05"
                d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            />
            <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            />
        </svg>
        {label}
    </button>
);

// ---------- Field components ----------
const PasswordField = ({ id, label, value, onChange, testId, autoComplete }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-sm">
                {label}
            </Label>
            <div className="relative">
                <Input
                    id={id}
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    data-testid={testId}
                    className="pr-10 h-11"
                />
                <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid={`${testId}-toggle`}
                    className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
        </div>
    );
};

const PasswordStrength = ({ password, testId = "password-strength" }) => {
    const score = scoreStrength(password);
    // Explicit tone → tailwind class map. Kept inline (not tailwind arbitrary
    // values) so purge picks it up and the palette stays reviewable.
    const TONE_CLASSES = {
        muted: "bg-border",
        destructive: "bg-destructive",
        amber: "bg-[#D4A038]",
        green: "bg-[#5E7153]",
        teal: "bg-[#2F8A80]",
    };
    // Fill % per tier (spec): 25 / 50 / 75 / 100.
    const FILL_PCT = { 0: 0, 1: 25, 2: 50, 3: 75, 4: 100 };
    const pct = FILL_PCT[score.value] || 0;
    return (
        <div
            data-testid={testId}
            data-tier={score.tier}
            data-strength={score.label ? score.label.toLowerCase() : "empty"}
            className="space-y-1.5"
        >
            <div className="h-1.5 rounded-full bg-border overflow-hidden">
                <div
                    data-testid={`${testId}-fill`}
                    style={{ width: `${pct}%` }}
                    className={cn(
                        "h-full rounded-full transition-all duration-200",
                        TONE_CLASSES[score.tone] || TONE_CLASSES.muted,
                    )}
                />
            </div>
            <div
                data-testid={`${testId}-hint`}
                className={cn(
                    "text-xs leading-relaxed transition-colors",
                    score.tier === "rejected"
                        ? "text-destructive"
                        : "text-muted-foreground",
                )}
            >
                {score.hint || "\u00A0"}
            </div>
        </div>
    );
};

// ============================================================================
// AuthPage
// ============================================================================
const MODES = {
    LOGIN: "login",
    SIGNUP: "signup",
    FORGOT: "forgot",
    RESET: "reset",
};

function parseHash() {
    if (typeof window === "undefined") return { mode: null, token: null };
    const h = (window.location.hash || "").replace(/^#\/?/, "");
    const [path, qs] = h.split("?");
    const params = new URLSearchParams(qs || "");
    if (path === "signup") return { mode: MODES.SIGNUP, token: null };
    if (path === "forgot-password") return { mode: MODES.FORGOT, token: null };
    if (path === "reset-password")
        return { mode: MODES.RESET, token: params.get("token") };
    return { mode: null, token: null };
}

export default function AuthPage() {
    const initial = parseHash();
    const [mode, setMode] = useState(initial.mode || MODES.LOGIN);
    const [resetToken, setResetToken] = useState(initial.token || "");
    const { login, signup, forgotPassword, resetPassword, startGoogleLogin } =
        useAuth();

    // Watch the hash so deep links into /#/forgot-password etc. flip the mode.
    useEffect(() => {
        const onHash = () => {
            const next = parseHash();
            if (next.mode) {
                setMode(next.mode);
                if (next.token) setResetToken(next.token);
            }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    return (
        <div className="min-h-screen flex bg-background text-foreground">
            <BrandPanel />
            <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md space-y-6">
                    {mode === MODES.LOGIN && (
                        <LoginForm
                            onLogin={login}
                            onGoogle={startGoogleLogin}
                            onSwitch={(m) => setMode(m)}
                        />
                    )}
                    {mode === MODES.SIGNUP && (
                        <SignupForm
                            onSignup={signup}
                            onGoogle={startGoogleLogin}
                            onSwitch={(m) => setMode(m)}
                        />
                    )}
                    {mode === MODES.FORGOT && (
                        <ForgotForm
                            onSend={forgotPassword}
                            onSwitch={(m) => setMode(m)}
                            onResetReady={(t) => {
                                setResetToken(t);
                                setMode(MODES.RESET);
                            }}
                        />
                    )}
                    {mode === MODES.RESET && (
                        <ResetForm
                            token={resetToken}
                            onReset={resetPassword}
                            onSwitch={(m) => setMode(m)}
                            onTokenChange={setResetToken}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- Login ----------
function LoginForm({ onLogin, onGoogle, onSwitch }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = email && password && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError("");
        setSubmitting(true);
        try {
            await onLogin(email.trim(), password);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="form-login"
            className="space-y-5"
            noValidate
        >
            <div className="space-y-1.5">
                <h2 className="font-serif text-3xl">Welcome back</h2>
                <p className="text-sm opacity-70">
                    Sign in to continue with your ToneGuard chats and ToneMirror.
                </p>
            </div>
            <GoogleButton onClick={onGoogle} label="Continue with Google" />
            <Divider />
            <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-sm">
                    Email
                </Label>
                <Input
                    id="login-email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="input-login-email"
                    className="h-11"
                />
            </div>
            <PasswordField
                id="login-password"
                label="Password"
                value={password}
                onChange={setPassword}
                testId="input-login-password"
                autoComplete="current-password"
            />
            {error && (
                <div
                    data-testid="login-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {error}
                </div>
            )}
            <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="btn-login-submit"
                className="w-full h-11"
            >
                {submitting ? "Signing in…" : "Login"}
            </Button>
            <div className="flex items-center justify-between text-sm">
                <button
                    type="button"
                    onClick={() => onSwitch(MODES.FORGOT)}
                    data-testid="link-forgot-password"
                    className="opacity-70 hover:opacity-100 underline-offset-4 hover:underline"
                >
                    Forgot password?
                </button>
                <button
                    type="button"
                    onClick={() => onSwitch(MODES.SIGNUP)}
                    data-testid="link-go-signup"
                    className="opacity-70 hover:opacity-100 underline-offset-4 hover:underline"
                >
                    Create account →
                </button>
            </div>
        </form>
    );
}

// ---------- Signup ----------
function SignupForm({ onSignup, onGoogle, onSwitch }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const passwordOk = useMemo(() => passwordAcceptable(password), [password]);
    const matches = password.length > 0 && password === confirm;
    const canSubmit = !!email && passwordOk && matches && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError("");
        setSubmitting(true);
        try {
            await onSignup(email.trim(), password, confirm);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="form-signup"
            className="space-y-5"
            noValidate
        >
            <div className="space-y-1.5">
                <h2 className="font-serif text-3xl">Create your account</h2>
                <p className="text-sm opacity-70">
                    Just an email + password. No name, no phone. Welcome.
                </p>
            </div>
            <GoogleButton onClick={onGoogle} label="Sign up with Google" />
            <Divider />
            <div className="space-y-1.5">
                <Label htmlFor="signup-email" className="text-sm">
                    Email
                </Label>
                <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="input-signup-email"
                    className="h-11"
                />
            </div>
            <PasswordField
                id="signup-password"
                label="Password"
                value={password}
                onChange={setPassword}
                testId="input-signup-password"
                autoComplete="new-password"
            />
            <PasswordStrength password={password} testId="password-strength" />
            <PasswordField
                id="signup-confirm"
                label="Confirm password"
                value={confirm}
                onChange={setConfirm}
                testId="input-signup-confirm"
                autoComplete="new-password"
            />
            {confirm.length > 0 && !matches && (
                <div className="text-xs text-destructive" data-testid="signup-mismatch">
                    Passwords do not match.
                </div>
            )}
            {error && (
                <div
                    data-testid="signup-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {error}
                </div>
            )}
            <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="btn-signup-submit"
                className="w-full h-11"
            >
                {submitting ? "Creating account…" : "Create account"}
            </Button>
            <div className="text-sm opacity-70">
                Already have an account?{" "}
                <button
                    type="button"
                    onClick={() => onSwitch(MODES.LOGIN)}
                    data-testid="link-go-login"
                    className="underline underline-offset-4 hover:opacity-100"
                >
                    Login
                </button>
            </div>
        </form>
    );
}

// ---------- Forgot password ----------
function ForgotForm({ onSend, onSwitch, onResetReady }) {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setError("");
        setSubmitting(true);
        try {
            const data = await onSend(email.trim());
            setSent(data);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div data-testid="forgot-sent" className="space-y-4">
                <h2 className="font-serif text-3xl">Check your inbox</h2>
                <p className="text-sm opacity-70">
                    If an account exists for{" "}
                    <span className="font-medium">{email}</span>, we created a
                    secure password reset link. The link expires in 1 hour.
                </p>
                {sent.reset_token && (
                    <div
                        data-testid="forgot-dev-token"
                        className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2"
                    >
                        <div className="text-xs uppercase tracking-wider opacity-60">
                            Dev mode — use this token to reset
                        </div>
                        <code className="block break-all text-xs font-mono">
                            {sent.reset_token}
                        </code>
                        <Button
                            type="button"
                            data-testid="btn-use-dev-token"
                            onClick={() => {
                                onResetReady(sent.reset_token);
                            }}
                            className="w-full h-10"
                        >
                            Continue to reset password
                        </Button>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => onSwitch(MODES.LOGIN)}
                    data-testid="link-back-to-login"
                    className="text-sm opacity-70 hover:opacity-100 underline-offset-4 hover:underline"
                >
                    ← Back to login
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="form-forgot"
            className="space-y-5"
            noValidate
        >
            <div className="space-y-1.5">
                <h2 className="font-serif text-3xl">Reset your password</h2>
                <p className="text-sm opacity-70">
                    Enter the email you signed up with and we&apos;ll send you a
                    secure link to set a new password.
                </p>
            </div>
            <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-sm">
                    Email
                </Label>
                <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="input-forgot-email"
                    className="h-11"
                />
            </div>
            {error && (
                <div
                    data-testid="forgot-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {error}
                </div>
            )}
            <Button
                type="submit"
                disabled={!email || submitting}
                data-testid="btn-forgot-submit"
                className="w-full h-11"
            >
                {submitting ? "Sending…" : "Send reset link"}
            </Button>
            <button
                type="button"
                onClick={() => onSwitch(MODES.LOGIN)}
                data-testid="link-back-to-login"
                className="text-sm opacity-70 hover:opacity-100 underline-offset-4 hover:underline"
            >
                ← Back to login
            </button>
        </form>
    );
}

// ---------- Reset password ----------
function ResetForm({ token, onReset, onSwitch, onTokenChange }) {
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const passwordOk = useMemo(() => passwordAcceptable(password), [password]);
    const matches = password.length > 0 && password === confirm;
    const canSubmit = !!token && passwordOk && matches && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError("");
        setSubmitting(true);
        try {
            await onReset(token, password, confirm);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="form-reset"
            className="space-y-5"
            noValidate
        >
            <div className="space-y-1.5">
                <h2 className="font-serif text-3xl">Set a new password</h2>
                <p className="text-sm opacity-70">
                    Pick a strong new password — same rules as signup.
                </p>
            </div>
            <div className="space-y-1.5">
                <Label htmlFor="reset-token" className="text-sm">
                    Reset token
                </Label>
                <Input
                    id="reset-token"
                    type="text"
                    value={token}
                    onChange={(e) => onTokenChange(e.target.value)}
                    data-testid="input-reset-token"
                    className="h-11 font-mono text-xs"
                />
            </div>
            <PasswordField
                id="reset-password"
                label="New password"
                value={password}
                onChange={setPassword}
                testId="input-reset-password"
                autoComplete="new-password"
            />
            <PasswordStrength password={password} testId="reset-password-strength" />
            <PasswordField
                id="reset-confirm"
                label="Confirm new password"
                value={confirm}
                onChange={setConfirm}
                testId="input-reset-confirm"
                autoComplete="new-password"
            />
            {confirm.length > 0 && !matches && (
                <div className="text-xs text-destructive" data-testid="reset-mismatch">
                    Passwords do not match.
                </div>
            )}
            {error && (
                <div
                    data-testid="reset-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {error}
                </div>
            )}
            <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="btn-reset-submit"
                className="w-full h-11"
            >
                {submitting ? "Updating…" : "Update password"}
            </Button>
            <button
                type="button"
                onClick={() => onSwitch(MODES.LOGIN)}
                data-testid="link-back-to-login"
                className="text-sm opacity-70 hover:opacity-100 underline-offset-4 hover:underline"
            >
                ← Back to login
            </button>
        </form>
    );
}

// ---------- Helpers ----------
const Divider = () => (
    <div className="flex items-center gap-3 text-xs opacity-60">
        <div className="flex-1 h-px bg-border" />
        <span>or</span>
        <div className="flex-1 h-px bg-border" />
    </div>
);
